import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Signal,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UpperFirstLetterPipe } from '../../pipes/upper-first-letter-pipe';
import { ActivatedRoute, ParamMap, Params, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { ApiHttpResponse, Country, Information } from '@kinomap-dev/ng-ui-lib/core';
import { CircularButtonIconComponent } from '@kinomap-dev/ng-ui-lib/buttons';
import { ApiService } from '../../services/api.service';
import { UpperCasePipe } from '@angular/common';
import { SkeletonComponent, IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { ContentPreview } from '../../components/content-creation-preview/content-preview';
import { ContentType } from '../content-creation/content-creation';
import { ContentGlobalStatsBlock } from '../../components/content-global-stats-block/content-global-stats-block';
import { ContentAvailabilityBlock } from '../../components/content-availability-block/content-availability-block';
import { ContentViewsDistributionBlock } from '../../components/content-views-distribution-block/content-views-distribution-block';
import { HttpResourceRef } from '@angular/common/http';
import { CountryService } from '../../services/country.service';
import { PaginatorService } from '../../services/paginator-service';
import { ModerationReason } from '../../types/moderation-reason.type';
import { ContentStatistics } from '../../types/content-statistics.type';
import {
  CountryViewerRepartition,
  ViewerRepartitionItem,
} from '../../types/country-viewer-repartition.type';
import { Toaster } from '../../services/toaster';

@Component({
  selector: 'app-content-details',
  imports: [
    TranslateModule,
    UpperFirstLetterPipe,
    UpperCasePipe,
    CircularButtonIconComponent,
    RouterLink,
    SkeletonComponent,
    ContentPreview,
    ContentGlobalStatsBlock,
    ContentAvailabilityBlock,
    ContentViewsDistributionBlock,
    IconComponent,
  ],
  templateUrl: './content-details.html',
  styleUrl: './content-details.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentDetails {
  private apiService: ApiService = inject(ApiService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private countryService: CountryService = inject(CountryService);
  private toaster: Toaster = inject(Toaster);
  private translateService: TranslateService = inject(TranslateService);
  private paginatorService: PaginatorService = inject(PaginatorService);

  private routeParams: Signal<ParamMap | undefined> = toSignal(this.activatedRoute.paramMap);

  readonly id: Signal<string | null> = computed(() => this.routeParams()?.get('id') || null);
  readonly type: Signal<ContentType | undefined> = computed(
    () => (this.routeParams()?.get('type') as ContentType) ?? undefined,
  );

  readonly backQueryParams: Signal<Params> = computed(() =>
    this.paginatorService.getListQueryParams(this.type()),
  );

  readonly contentRessource: HttpResourceRef<Information> =
    this.apiService.getContentDetailsResource(this.type(), this.id());
  readonly content: Signal<Information | undefined> = computed(() =>
    this.contentRessource.hasValue() ? this.contentRessource.value() : undefined,
  );

  readonly viewerRepartitionRessource: HttpResourceRef<
    ApiHttpResponse<ViewerRepartitionItem[]> | undefined
  > = this.apiService.getViewerRepartition(this.type(), this.id());
  readonly viewerRepartition: Signal<CountryViewerRepartition[] | undefined> = computed(() =>
    (this.viewerRepartitionRessource.hasValue()
      ? this.viewerRepartitionRessource.value()
      : undefined
    )?.data.map(({ viewers_count, cnt_id }: ViewerRepartitionItem) => {
      return {
        viewers_count: viewers_count,
        country: this.countryService.countries()?.find((country: Country) => country.id === cnt_id),
      };
    }),
  );

  readonly contentStatisticsRessource: HttpResourceRef<ContentStatistics | undefined> =
    this.apiService.getContentStatistics(this.type(), this.id());
  readonly contentStatistics: Signal<ContentStatistics | undefined> = computed(() =>
    this.contentStatisticsRessource.hasValue()
      ? this.contentStatisticsRessource.value()
      : undefined,
  );

  readonly contentModerationRefusedReasonsRessource: HttpResourceRef<
    ModerationReason[] | undefined
  > = this.apiService.getContentModerationRefusedReasons(this.id());
  readonly contentModerationRefusedReasons: Signal<ModerationReason[] | undefined> = computed(() =>
    this.contentModerationRefusedReasonsRessource.hasValue()
      ? this.contentModerationRefusedReasonsRessource.value()
      : undefined,
  );

  readonly moderationRefusedReasonsRessource: HttpResourceRef<ModerationReason[] | undefined> =
    this.apiService.getModerationRefusedReasons();
  readonly moderationRefusedReasons: Signal<ModerationReason[] | undefined> = computed(() =>
    this.moderationRefusedReasonsRessource.hasValue()
      ? this.moderationRefusedReasonsRessource.value()
      : undefined,
  );

  constructor() {
    this.toastOnError(this.contentRessource, 'content_details.errors.content');
    this.toastOnError(this.viewerRepartitionRessource, 'content_details.errors.views_distribution');
    this.toastOnError(this.contentStatisticsRessource, 'content_details.errors.statistics');
    this.toastOnError(
      this.contentModerationRefusedReasonsRessource,
      'content_details.errors.refusal_reasons',
    );
    this.toastOnError(
      this.moderationRefusedReasonsRessource,
      'content_details.errors.moderation_reasons',
    );
  }

  private toastOnError(resource: HttpResourceRef<unknown>, message: string): void {
    effect(() => {
      const error: unknown = resource.error();
      if (!error) return;
      console.error(message, error);
      this.toaster.error(
        this.translateService.instant('common.error'),
        this.translateService.instant(message),
        'warning',
      );
    });
  }
}
